// CSV download helper. Filename convention:
//   <prefix>_<cohort-context>_<YYYY-MM-DD>.csv
// where cohort-context is e.g. "2026-04" or "2026-04-01_to_2026-04-15".

import type { Filters } from "./filters";

export function cohortContext(filters: Filters | null | undefined): string {
  if (!filters) return "all";
  if (filters.granularity === "day" && (filters.cohortDayFrom || filters.cohortDayTo)) {
    const from = filters.cohortDayFrom ?? filters.cohortDayTo;
    const to = filters.cohortDayTo ?? filters.cohortDayFrom;
    if (from && to && from !== to) return `${from}_to_${to}`;
    return from ?? "all";
  }
  if (filters.months.length === 0) return "all-time";
  if (filters.months.length === 1) return filters.months[0] ?? "all";
  return `${filters.months.length}-months`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function escapeCell(v: unknown): string {
  if (v == null) return "";
  const s = typeof v === "string" ? v : Array.isArray(v) || typeof v === "object" ? JSON.stringify(v) : String(v);
  if (s.includes(",") || s.includes("\"") || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, "\"\"")}"`;
  }
  return s;
}

export function downloadCsv(
  rows: Record<string, unknown>[],
  columns: string[],
  filenamePrefix: string,
  filters?: Filters | null,
): void {
  const header = columns.map(escapeCell).join(",");
  const body = rows.map((r) => columns.map((c) => escapeCell(r[c])).join(",")).join("\n");
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenamePrefix}_${cohortContext(filters)}_${todayIso()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Variant for the QueryInspector's raw-rows tab, which holds rows as `unknown[][]`
// alongside a `columns: string[]` header.
export function downloadCsvRowArrays(
  rows: unknown[][],
  columns: string[],
  filenamePrefix: string,
  filters?: Filters | null,
): void {
  const header = columns.map(escapeCell).join(",");
  const body = rows.map((r) => r.map(escapeCell).join(",")).join("\n");
  const csv = `${header}\n${body}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenamePrefix}_${cohortContext(filters)}_${todayIso()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
